import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trophy, LogOut, Shield, User, Building2, X, Mail, Calendar, Users, MapPin, Edit2, Save, Phone, Camera, ChevronDown } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

import { auth, db } from '../../services/firebase';
import { authService } from '../../services/authService';
import { updateEmail } from 'firebase/auth';
import { formatDate } from '../../utils/helpers';

import JoinClubForm from '../player/JoinClubForm';

export const JoinAnotherClubModal = ({ user, onClose }) => {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800">Join New Club</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
            <X size={20} className="text-slate-500" />
          </button>
        </div>
        <div className="p-6">
          <JoinClubForm 
            user={user} 
            onJoinSuccess={() => {
              alert("Successfully requested to join the club! Waiting for admin approval.");
              onClose();
            }} 
          />
        </div>
      </div>
    </div>
  );
};

export const SwitchClubModal = ({ user, onClose }) => {
  const [switching, setSwitching] = useState(false);
  const [clubNames, setClubNames] = useState({});
  const [deletedClubs, setDeletedClubs] = useState(new Set());
  const [checking, setChecking] = useState(true);

  // Fetch actual club names from Firestore on mount
  useEffect(() => {
    const fetchClubNames = async () => {
      const names = {};
      const missing = new Set();
      const actualClubIds = Array.from(new Set([
        ...(user?.joinedClubsIds || []),
        ...(user?.joinedClubs ? Object.keys(user.joinedClubs) : []),
        user?.clubId,
        user?.clubInfo?.clubId
      ].filter(Boolean)));
      for (const cId of actualClubIds) {
        try {
          const clubRef = doc(db, 'club', cId);
          const clubSnap = await getDoc(clubRef);
          if (clubSnap.exists()) {
            names[cId] = clubSnap.data().name;
          } else {
            missing.add(cId);
          }
        } catch (e) {
          console.error('Error fetching club name for', cId, e);
          missing.add(cId);
        }
      }
      setClubNames(names);
      setDeletedClubs(missing);
      setChecking(false);
    };
    fetchClubNames();
  }, [user?.joinedClubsIds]);

  const handleSwitch = async (clubId) => {
    setSwitching(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const newClubInfo = { ...user.joinedClubs[clubId] };
      
      // Use the actual club name from Firestore (not the stale joinedClubs copy)
      if (clubNames[clubId]) {
        newClubInfo.clubName = clubNames[clubId];
      }
      
      // Determine the effective role for the new club
      const perClubRole = newClubInfo?.role || 'player';
      
      // Update their legacy active fields without explicitly demoting their global role
      await updateDoc(userRef, {
        clubId: clubId,
        clubInfo: newClubInfo,
        // Also fix the stale club name in joinedClubs
        ...(clubNames[clubId] ? { [`joinedClubs.${clubId}.clubName`]: clubNames[clubId] } : {})
      });
      // Force reload the page so the app boots into the newly active club!
      window.location.href = '/dashboard';
    } catch(e) {
      console.error(e);
      alert("Failed to switch club");
      setSwitching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800">Switch Club</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
            <X size={20} className="text-slate-500" />
          </button>
        </div>
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {checking && <div className="text-center py-4 font-medium text-slate-600 animate-pulse">Verifying clubs...</div>}
          {switching && <div className="text-center py-4 font-medium text-slate-600">Switching active club...</div>}
          {!checking && !switching && (() => {
            const actualClubIds = Array.from(new Set([
              ...(user?.joinedClubsIds || []),
              ...(user?.joinedClubs ? Object.keys(user.joinedClubs) : []),
              user?.clubId,
              user?.clubInfo?.clubId
            ].filter(Boolean)));
            return actualClubIds.map(cId => {
              if (deletedClubs.has(cId)) return null;
            const inf = user.joinedClubs[cId];
            // Use fresh club name from Firestore, fallback to joinedClubs copy
            const displayName = clubNames[cId] || inf?.clubName || 'Unknown Club';
            return (
              <div key={cId} onClick={() => handleSwitch(cId)} className={`p-4 border rounded-xl mb-3 cursor-pointer transition-all ${user.clubId === cId ? 'border-green-500 bg-green-50 ring-2 ring-green-500 ring-opacity-20' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                <div className="flex justify-between items-center mb-1">
                  <div className="font-bold text-slate-800">{displayName}</div>
                  {user.clubId === cId && <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-semibold">Active</span>}
                </div>
                <div className="text-sm text-slate-500 capitalize">Role: {inf?.role || 'player'} | Status: {inf?.status}</div>
              </div>
            );
            });
          })()}
          
          {!checking && !switching && (() => {
            const actualClubIds = Array.from(new Set([
              ...(user?.joinedClubsIds || []),
              ...(user?.joinedClubs ? Object.keys(user.joinedClubs) : []),
              user?.clubId,
              user?.clubInfo?.clubId
            ].filter(Boolean)));
            return actualClubIds.length > 0 && deletedClubs.size === actualClubIds.length && (
              <div className="text-center py-4 font-medium text-slate-600">All of your clubs have been deleted. Valid clubs required.</div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// Separate ProfileModal component outside of Header
export const ProfileModal = ({
  user,
  profilePicture,
  userName,
  userInitial,
  roleBadge,
  RoleIcon,
  clubName,
  clubLogo,
  teamName,
  playerData,
  onClose
}) => {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editName, setEditName] = useState(user?.name || user?.displayName || '');
  const [editEmail, setEditEmail] = useState(user?.email || '');
  const [editPhoneNumber, setEditPhoneNumber] = useState(user?.phoneNumber || '');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newProfilePicture, setNewProfilePicture] = useState(null);

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');
    setUploadingPhoto(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;

        // Update user document with new profile picture
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          profilePicture: base64String
        });

        setNewProfilePicture(base64String);
        setSuccess('Profile picture updated successfully!');

        // Reload after a delay to show the new picture
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError('Failed to upload profile picture. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    let emailChanged = false;

    try {
      // If email has changed, attempt to update Firebase Auth first
      if (editEmail.trim() && editEmail.trim() !== user?.email) {
        const authResponse = await authService.updateUserEmail(editEmail.trim());
        if (!authResponse.success) {
          throw new Error(authResponse.error || 'Failed to update email address.');
        }
        emailChanged = true;
      }

      const userRef = doc(db, 'users', user.uid);
      
      // Update email in Firebase Auth if it changed
      let emailChanged = false;
      const newEmail = editEmail.trim();
      if (newEmail !== user?.email) {
        if (!newEmail || !newEmail.includes('@')) {
          setError('Please provide a valid email address.');
          setSaving(false);
          return;
        }
        
        try {
          if (auth.currentUser) {
            await updateEmail(auth.currentUser, newEmail);
            emailChanged = true;
          }
        } catch (authError) {
          console.error('Firebase Auth Email Update Error:', authError);
          if (authError.code === 'auth/requires-recent-login') {
            setError('For security reasons, changing your email requires a recent login. Please log out and sign back in to change your email.');
          } else {
            setError(authError.message || 'Failed to update email in authentication server.');
          }
          setSaving(false);
          return;
        }
      }

      // Prepare Firestore payload
      const payload = {
        name: editName.trim(),
        phoneNumber: editPhoneNumber.trim()
      };
      
      if (emailChanged) {
        payload.email = newEmail;
      }

      await updateDoc(userRef, payload);

      setSuccess('Profile updated successfully! ' + (emailChanged ? 'A verification link was sent to your new address. Please click it to finalize the email change! ' : '') + (!emailChanged ? 'Please refresh the page to see changes.' : ''));
      setEditMode(false);

      // Don't reload - just show success and close after delay
      setTimeout(() => {
        onClose();
      }, 5000);
    } catch (err) {
      console.error('Error updating profile:', err);
      // Only set error if it hasn't been set by authService explicitly as an Error object
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditName(user?.name || user?.displayName || '');
    setEditEmail(user?.email || '');
    setEditPhoneNumber(user?.phoneNumber || '');
    setError('');
    setSuccess('');
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !editMode) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-8 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>

          {/* Avatar */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              {newProfilePicture || profilePicture ? (
                <img
                  src={newProfilePicture || profilePicture}
                  alt={userName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white/20 shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center border-4 border-white/20 shadow-lg">
                  <span className="text-4xl font-bold text-white">{userInitial}</span>
                </div>
              )}

              {/* Upload Button Overlay */}
              <label
                htmlFor="profile-picture-upload"
                className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors border-2 border-white"
              >
                {uploadingPhoto ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Camera className="w-4 h-4 text-white" />
                )}
              </label>
              <input
                id="profile-picture-upload"
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
                disabled={uploadingPhoto}
              />
            </div>
          </div>

          {/* Name */}
          {editMode ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="text-xl font-bold text-gray-900 bg-white px-4 py-2 rounded-lg mb-2 text-center w-full max-w-xs mx-auto focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="Your Name"
            />
          ) : (
            <h2 className="text-xl font-bold text-white mb-2">{userName}</h2>
          )}

          {/* Role Badge */}
          <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-full ${roleBadge.bg} ${roleBadge.text}`}>
            <RoleIcon className="w-4 h-4" />
            {roleBadge.label}
          </span>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Profile Details */}
        <div className="p-6 space-y-4">
          {/* Email */}
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 font-medium">Email (Login Identity)</p>
              {editMode ? (
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="text-sm font-semibold text-gray-900 bg-white px-3 py-1 rounded border border-gray-300 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                  placeholder="example@email.com"
                />
              ) : (
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.email || 'N/A'}</p>
              )}
            </div>
          </div>

          {/* Phone Number */}
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 font-medium">Phone Number</p>
              {editMode ? (
                <input
                  type="tel"
                  value={editPhoneNumber}
                  onChange={(e) => setEditPhoneNumber(e.target.value)}
                  className="text-sm font-semibold text-gray-900 bg-white px-3 py-1 rounded border border-gray-300 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                  placeholder="(123) 456-7890"
                />
              ) : (
                <p className="text-sm font-semibold text-gray-900">{user?.phoneNumber || 'Not provided'}</p>
              )}
            </div>
          </div>

          {/* Club */}
          {clubName && (
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {clubLogo ? (
                  <img src={clubLogo} alt={clubName} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-5 h-5 text-amber-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium">Club</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{clubName}</p>
              </div>
            </div>
          )}

          {/* Location */}
          {(user?.clubInfo?.clubLocation || playerData?.clubLocation) && (
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium">Location</p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user?.clubInfo?.clubLocation || playerData?.clubLocation}
                </p>
              </div>
            </div>
          )}

          {/* Member Since */}
          {(user?.createdAt || user?.clubInfo?.joinedAt) && (
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium">Member Since</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(user?.clubInfo?.joinedAt || user?.createdAt)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-3">
          {editMode ? (
            <>
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditMode(true)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Edit2 size={18} />
                Edit Profile
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Header = ({ user, role, onLogout, clubName, clubLogo, teamName, onBackToClubs, playerData, notifications = [], onNotificationClick, isUserAdmin, canSwitchToAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfile, setShowProfile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showJoinClubModal, setShowJoinClubModal] = useState(false);
  const [showSwitchClubModal, setShowSwitchClubModal] = useState(false);

  // Get profile picture from user or playerData
  const profilePicture = user?.profilePicture || playerData?.profilePicture;
  const userName = user?.name || user?.displayName || playerData?.name || user?.email?.split('@')[0] || 'User';
  const userInitial = userName[0].toUpperCase();

  // Role badge styling
  const getRoleBadge = () => {
    switch (role) {
      case 'super-admin':
        return {
          bg: 'bg-gradient-to-r from-purple-500 to-indigo-500',
          text: 'text-white',
          icon: Shield,
          label: 'Super Admin'
        };
      case 'admin':
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-cyan-500',
          text: 'text-white',
          icon: Shield,
          label: 'Admin'
        };
      case 'captain':
        return {
          bg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
          text: 'text-white',
          icon: Trophy,
          label: 'Captain'
        };
      case 'treasurer':
        return {
          bg: 'bg-gradient-to-r from-yellow-600 to-amber-600',
          text: 'text-white',
          icon: Building2,
          label: 'Treasurer'
        };
      case 'auditor':
        return {
          bg: 'bg-gradient-to-r from-purple-500 to-pink-500',
          text: 'text-white',
          icon: Shield,
          label: 'Auditor'
        };
      case 'player':
        return {
          bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
          text: 'text-white',
          icon: User,
          label: 'Player'
        };
      default:
        // Check additionalRoles from user object if role is not explicitly handled
        if (user?.additionalRoles?.includes('treasurer') || user?.clubInfo?.additionalRoles?.includes('treasurer')) {
          return {
            bg: 'bg-gradient-to-r from-yellow-600 to-amber-600',
            text: 'text-white',
            icon: Building2,
            label: 'Treasurer'
          };
        }
        if (user?.additionalRoles?.includes('auditor') || user?.clubInfo?.additionalRoles?.includes('auditor')) {
          return {
            bg: 'bg-gradient-to-r from-purple-500 to-pink-500',
            text: 'text-white',
            icon: Shield,
            label: 'Auditor'
          };
        }
        return {
          bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
          text: 'text-white', // Fixed: was missing icon
          icon: User,
          label: 'Player'
        };
    }
  };

  const roleBadge = getRoleBadge();
  const RoleIcon = roleBadge.icon;

  // Handle logo click - always go to player view
  const handleLogoClick = () => {
    navigate('/dashboard');
  };



  return (
    <>
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Logo Section - Clickable */}
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer flex-shrink-0"
              title="Go to Player View"
            >
              <div className="relative">
                <div className="w-11 h-11 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/20">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-blue-500 rounded-full border-2 border-slate-900"></div>
              </div>
              <div className="flex flex-col text-left">
                <h1 className="text-2xl font-bold leading-none tracking-tight">
                  <span className="text-green-400">Club</span>
                  <span className="text-blue-400">Hudl</span>
                </h1>
                <p className="text-[10px] text-slate-400 font-medium tracking-wider uppercase">
                  Your Club Operations Made Easy
                </p>
              </div>
            </button>

            {/* Center - Club Info (if available) */}
            {clubName && (
              <div className="hidden md:flex items-center gap-3 px-5 py-2 bg-slate-700/50 rounded-xl border border-slate-600/50 flex-shrink truncate mx-auto">
                <div className="w-9 h-9 flex-shrink-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center overflow-hidden">
                  {clubLogo ? (
                    <img src={clubLogo} alt={clubName} className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{clubName}</p>
                </div>
              </div>
            )}

            {/* Right Section - User Info */}
            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
              {/* Notification Bell */}
              <button
                onClick={onNotificationClick}
                className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-600 rounded-lg transition-all duration-200"
                title="Notifications"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notifications && notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                    {notifications.length}
                  </span>
                )}
              </button>

              {/* User Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 md:gap-3 pl-3 pr-2 py-2 bg-slate-700/50 rounded-xl border border-slate-600/50 hover:bg-slate-600/50 transition-colors cursor-pointer"
                >
                  {/* Avatar - Show photo or initial */}
                  {profilePicture ? (
                    <img
                      src={profilePicture}
                      alt={userName}
                      className="w-8 h-8 md:w-9 md:h-9 rounded-lg object-cover border border-slate-500/50"
                    />
                  ) : (
                    <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-slate-600 to-slate-700 rounded-lg flex items-center justify-center border border-slate-500/50">
                      <span className="text-sm font-bold text-white">{userInitial}</span>
                    </div>
                  )}

                  {/* Name & Role */}
                  <div className="hidden sm:block text-left pr-1">
                    <p className="text-sm font-semibold text-white leading-tight">{userName}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${roleBadge.bg} ${roleBadge.text}`}>
                        <RoleIcon className="w-3 h-3" />
                        {roleBadge.label}
                      </span>
                    </div>
                  </div>
                  
                  {/* Dropdown Icon */}
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsDropdownOpen(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden transform origin-top-right transition-all">
                      <div className="py-1">
                        {/* Profile Item */}
                        <button
                          onClick={() => {
                            setShowProfile(true);
                            setIsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-3 font-medium"
                        >
                          <User size={16} className="text-slate-500" />
                          View Profile
                        </button>

                        {/* View Switch Item */}
                        {(() => {
                          const activeClubId = user?.clubId || user?.clubInfo?.clubId;
                          const isNativeClubAdmin = user?.permissions?.clubAdminOf?.includes(activeClubId);
                          
                          const legacyClubId = user?.clubId || user?.clubInfo?.clubId;
                          const isLegacyClubMatch = !activeClubId || activeClubId === legacyClubId;

                          // Use injected props if available, otherwise fallback
                          const hasAdminAccess = canSwitchToAdmin !== undefined 
                            ? canSwitchToAdmin 
                            : (user?.role === 'super-admin' || 
                               (isLegacyClubMatch && (user?.role === 'admin' || user?.clubInfo?.role === 'admin' ||
                               user?.role === 'treasurer' || user?.clubInfo?.role === 'treasurer' ||
                               user?.additionalRoles?.includes('treasurer') || user?.clubInfo?.additionalRoles?.includes('treasurer'))) ||
                               isNativeClubAdmin);

                          const isCurrentlyAdmin = location.pathname.includes('/admin');
                          
                          // Determine the correct label
                          // Fallback to legacy effective role if props aren't provided
                          const activeClubRole = activeClubId && user?.joinedClubs?.[activeClubId]?.role;
                          const effectiveRole = activeClubRole || (isLegacyClubMatch ? (user?.clubInfo?.role || user?.role) : null);
                          const isGlobalAdmin = user?.role === 'super-admin' || (isLegacyClubMatch && user?.role === 'admin');
                          
                          let btnLabel;
                          if (isCurrentlyAdmin) {
                            btnLabel = 'Switch to Player View';
                          } else if (isUserAdmin !== undefined) {
                            // Derive precisely from upstream
                            btnLabel = isUserAdmin ? 'Admin View' : 'Treasurer View';
                          } else if (effectiveRole === 'admin' || isNativeClubAdmin || isGlobalAdmin) {
                            btnLabel = 'Admin View';
                          } else if (effectiveRole === 'treasurer' || user?.additionalRoles?.includes('treasurer')) {
                            btnLabel = 'Treasurer View';
                          } else {
                            btnLabel = 'Admin View';
                          }
                          
                          const Icon = isCurrentlyAdmin ? User : Shield;
                          
                          if (!hasAdminAccess && !isCurrentlyAdmin) return null;
                          
                          return (
                            <button
                              onClick={() => {
                                navigate(isCurrentlyAdmin ? '/dashboard' : '/admin');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                            >
                              <Icon size={16} className={isCurrentlyAdmin ? "text-slate-500" : "text-purple-500"} />
                              {btnLabel}
                            </button>
                          );
                        })()}

                        {/* Switch Club Item */}
                        {(() => {
                          const actualClubIds = Array.from(new Set([
                            ...(user?.joinedClubsIds || []),
                            ...(user?.joinedClubs ? Object.keys(user.joinedClubs) : []),
                            user?.clubId,
                            user?.clubInfo?.clubId
                          ].filter(Boolean)));

                          if (actualClubIds.length <= 1) return null;

                          return (
                            <button
                              onClick={() => {
                                setShowSwitchClubModal(true);
                                setIsDropdownOpen(false);
                              }}
                              className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                            >
                              <Building2 size={16} className="text-blue-500" />
                              Switch Club
                            </button>
                          );
                        })()}

                        {/* Join New Club Item */}
                        <button
                          onClick={() => {
                            setShowJoinClubModal(true);
                            setIsDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                        >
                          <Trophy size={16} className="text-green-500" />
                          Join New Club
                        </button>

                        {/* Logout Item */}
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false);
                            onLogout();
                          }}
                          className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3 font-medium border-t border-slate-100"
                        >
                          <LogOut size={16} className="text-red-500" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      {showProfile && (
        <ProfileModal
          user={user}
          profilePicture={profilePicture}
          userName={userName}
          userInitial={userInitial}
          roleBadge={roleBadge}
          RoleIcon={RoleIcon}
          clubName={clubName}
          clubLogo={clubLogo}
          teamName={teamName}
          playerData={playerData}
          onClose={() => setShowProfile(false)}
        />
      )}

      {showJoinClubModal && (
        <JoinAnotherClubModal
          user={user}
          onClose={() => setShowJoinClubModal(false)}
        />
      )}

      {showSwitchClubModal && (
        <SwitchClubModal
          user={user}
          onClose={() => setShowSwitchClubModal(false)}
        />
      )}
    </>
  );
};

export default Header;